import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import {
  GUEST_CREATE_ACCOUNT_SIGNUP_PATH,
  GUEST_TRIAL_ENDED_SIGNUP_PATH,
} from '../guest-trial-cta';

/**
 * Listens for the `fias:credits-exhausted` window event and navigates the
 * user to the credits store via react-router (preserving SPA state).
 *
 * The event is dispatched by the `client/src/lib/api.ts` request interceptor
 * when the API returns 402 INSUFFICIENT_CREDITS with allBucketsExhausted=true.
 * (Streaming errors no longer navigate — each chat surface renders the shared
 * `InsufficientCreditsMessage` inline instead, which is guest-aware too.)
 *
 * A guest-trial account cannot buy credits (its trial is a one-time grant), so
 * a guest is sent to sign up — `GUEST_TRIAL_ENDED_SIGNUP_PATH` — instead of the
 * credits store.
 *
 * Centralizing the navigation here means there's exactly one place that
 * decides whether to navigate, the navigation is router-aware (no full
 * page reload), and we never trigger a redirect loop while already on
 * the credits page.
 *
 * Mount this component once, inside the router context, near the top of
 * the app tree.
 */
export function CreditExhaustionListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuest } = useAuth();

  useEffect(() => {
    // Guests can't buy credits — trial exhaustion routes them to signup instead
    // of the credits store. Full accounts go to /credits as before.
    const exhaustedTarget = isGuest
      ? GUEST_TRIAL_ENDED_SIGNUP_PATH
      : '/credits?reason=out-of-credits';
    const exhaustedSkip = isGuest ? '/auth/signup' : '/credits';
    const onExhausted = () => {
      if (location.pathname.startsWith(exhaustedSkip)) return;
      navigate(exhaustedTarget);
    };

    // Guest hit a full-account-only surface (requireFullAccount → 403). Only
    // guests receive that event; route them to signup.
    const onGuestRestricted = () => {
      if (location.pathname.startsWith('/auth/signup')) return;
      navigate(GUEST_CREATE_ACCOUNT_SIGNUP_PATH);
    };

    window.addEventListener('fias:credits-exhausted', onExhausted);
    window.addEventListener('fias:guest-restricted', onGuestRestricted);
    return () => {
      window.removeEventListener('fias:credits-exhausted', onExhausted);
      window.removeEventListener('fias:guest-restricted', onGuestRestricted);
    };
  }, [navigate, location.pathname, isGuest]);

  return null;
}
